import { clearPersistedSmartFormChat } from './smartFormChatCache';
import { fetchFormDefinition, invalidateFormDefinitionCache } from './workflow';
import { Model } from 'survey-core';
export const FORM_SESSION_TTL_MS = 60 * 60 * 1000;
const formSessions = new Map();
/** Coalesce concurrent first-loads so two callers never create two Models. */
const inFlightLoads = new Map();
function isSessionValid(session) {
    return !!session && Date.now() - session.fetchedAt < FORM_SESSION_TTL_MS;
}
/** Returns the in-memory session when still within TTL (no network). */
export function getValidFormSession(sessionKey) {
    const session = formSessions.get(sessionKey);
    if (!isSessionValid(session)) {
        if (session) {
            formSessions.delete(sessionKey);
        }
        return null;
    }
    return session;
}
export function invalidateFormSession(sessionKey, invalidateDefinition = false) {
    if (sessionKey) {
        const session = formSessions.get(sessionKey);
        formSessions.delete(sessionKey);
        inFlightLoads.delete(sessionKey);
        clearPersistedSmartFormChat(sessionKey);
        if (invalidateDefinition) {
            invalidateFormDefinitionCache(session?.formId ?? sessionKey);
        }
        return;
    }
    formSessions.clear();
    inFlightLoads.clear();
    clearPersistedSmartFormChat();
    if (invalidateDefinition) {
        invalidateFormDefinitionCache();
    }
}
export async function getOrLoadFormSession(sessionKey, options = {}) {
    const formId = options.formId ?? sessionKey;
    const existing = getValidFormSession(sessionKey);
    if (existing) {
        return { detail: existing.detail, survey: existing.survey, fromCache: true };
    }
    const pending = inFlightLoads.get(sessionKey);
    if (pending) {
        return pending;
    }
    const loadPromise = (async () => {
        // Re-check after awaiting any gap — another caller may have finished.
        const raced = getValidFormSession(sessionKey);
        if (raced) {
            return { detail: raced.detail, survey: raced.survey, fromCache: true };
        }
        const detail = await fetchFormDefinition(formId);
        const afterFetch = getValidFormSession(sessionKey);
        if (afterFetch) {
            return { detail: afterFetch.detail, survey: afterFetch.survey, fromCache: true };
        }
        const survey = new Model(detail.survey);
        if (options.allowSubmit) {
            survey.completeText = 'Prepare for submit';
            survey.showCompleteButton = true;
        }
        else {
            survey.showCompleteButton = false;
        }
        formSessions.set(sessionKey, {
            sessionKey,
            formId,
            detail,
            survey,
            fetchedAt: Date.now(),
        });
        return { detail, survey, fromCache: false };
    })().finally(() => {
        inFlightLoads.delete(sessionKey);
    });
    inFlightLoads.set(sessionKey, loadPromise);
    return loadPromise;
}
